import jwtDecode from 'jwt-decode';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { loginWithToken } from '../../api/generated-evs';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useSnackbars } from '../../hooks/useSnackbars';
import LoadingBackdrop from '../../components/LoadingBackdrop';

import { authTokenKey, login, setLoginInProgress } from './authService';

export interface User {
  email: string;
  role: string;
  firstName: string;
  lastName: string;
  businessUnits: string[];
  exp: number;
}

interface AuthContextType {
  isAuthenticated: boolean;
  jwt: string | null;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { showErrorSnackbar } = useSnackbars();
  const [jwt, setJwt] = useLocalStorage(authTokenKey, null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const loginStarted = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const loginToken = params.get('loginToken') ?? undefined;

  const logout = useCallback(() => {
    setJwt(null);
    login();
  }, [setJwt]);

  useEffect(() => {
    if (loginToken && !loginStarted.current) {
      loginStarted.current = true;
      setIsLoggingIn(true);
      setLoginInProgress(true);
      loginWithToken({ body: { portalToken: loginToken } })
        .then((res) => {
          const token = res.data?.appToken;
          setJwt(token);
          params.delete('loginToken');
          navigate(`${location.pathname}?${params.toString()}`, { replace: true });
          loginStarted.current = false;
        })
        .catch((err) => {
          console.error(err);
          showErrorSnackbar('Nastala chyba při přihlášení. Zkuste se znovu přihlásit.', {
            persist: true,
          });
        })
        .finally(() => {
          setIsLoggingIn(false);
          setLoginInProgress(false);
        });
    }
  }, [loginToken, location.pathname, navigate, setJwt, showErrorSnackbar]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!jwt && !isLoggingIn && !loginToken) {
      login();
    }

    if (jwt) {
      const data = jwtDecode<User>(jwt);
      if (data.exp && data.exp < Date.now() / 1000) {
        logout();
      }
    }
  }, [jwt, isLoggingIn, loginToken, logout]);

  // Block children from rendering until login flow completes.
  // This is evaluated DURING render (not in an effect), so children
  // never mount and fire API requests before the token is available.
  if (!!loginToken || isLoggingIn) {
    return <LoadingBackdrop isLoading />;
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!jwt,
        jwt,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuthentication = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuthentication must be used within AuthProvider');

  if (!context.jwt) {
    return {
      isAuthenticated: false,
      user: null,
      logout: context.logout,
    };
  }

  const user = jwtDecode<User>(context.jwt);

  return {
    isAuthenticated: context.isAuthenticated,
    user,
    logout: context.logout,
  };
};
